import React from 'react'
import { Box, Typography } from '@mui/material'
import { useFormState, useWatch } from 'react-hook-form'
import { FormSectionTitle, PairingButton } from 'src/Libs/flow/components'
import { checkTrueRecursively, logError } from 'src/Libs/flow/utils'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import useModals from 'src/Hooks/useModals'
import { deleteIndividualDevice } from 'src/Libs/flow/installation/api/save-installation/get-installation-delete-requests'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { HideDirtyStateProvider } from '../../providers'
import { getDeviceDefaultName } from 'src/Libs/flow/parsers'
import { NumberField, TextField } from 'src/Libs/inputs/react-hook-form'
import useAccordionStyle from 'src/Libs/styles'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { SettingsBox } from 'src/Elements/Data'
import { TextField as ShellTextField } from '@e3dc-react/shell/Elements/InputFields'
import { getPairingButtonLabel } from '../utils'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

export const StorageIo: React.FC = () => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()
  const authFetch = useAuthFetchWithResponse()

  const { classes } = useAccordionStyle()

  const { control, getValues, setValue, reset } = useInstallationForm()
  const { dirtyFields, errors } = useFormState({ control, name: 'ioInterfaces.storage' })

  const isDirty = checkTrueRecursively(dirtyFields.ioInterfaces?.storage)
  const isError = !!Object.keys(errors.ioInterfaces?.storage ?? {}).length

  const storageDevices = useWatch({ control, name: 'storageDevices' })
  const currentStorageIoInterface = useWatch({ control, name: 'ioInterfaces.storage' })
  const linkedStorageDevice = storageDevices.find(
    storage =>
      (storage.pairingId !== '' && storage.pairingId === currentStorageIoInterface?.pairingId) ||
      storage.deviceId === currentStorageIoInterface?.storageDeviceLinkId?.toString()
  )

  const [isNew, name, pairingId, deviceId, navigationId] = useWatch({
    control,
    name: [
      'ioInterfaces.storage.isNew',
      'ioInterfaces.storage.name',
      'ioInterfaces.storage.pairingId',
      'ioInterfaces.storage.deviceId',
      'ioInterfaces.storage.navigationId',
    ],
  })

  const displayName = getDeviceDefaultName(name, 'Storage I/O', deviceId, t)

  const { openConfirmationModal, openInstallationDeleteDialog } = useModals()
  const handleDelete = (): void => {
    openConfirmationModal({
      title: t('installation.deleteConfirmation.title'),
      text: `${t('installation.deleteConfirmation.selectedDevices')} ${displayName}`,
      buttonText: [t('common.cancel'), t('common.confirm')],
      onConfirm: async (onClose): Promise<void> => {
        const device = getValues('ioInterfaces.storage')
        if (!device) {
          logError('Storage I/O was not found and could not be deleted.')
          return
        }

        Promise.resolve().then(() => {
          deleteIndividualDevice(device, getValues, setValue, openInstallationDeleteDialog, reset, t, authFetch)
        })
        onClose()
      },
    })
  }

  return (
    <HideDirtyStateProvider hideDirtyState={isNew}>
      <Box component={'section'} id={navigationId}>
        <AccordionItem
          title={<FormSectionTitle name={displayName} onClick={handleDelete} isDirty={isDirty} isError={isError} />}
          expanded
          className={classes.accordion}
          sx={{ margin: 0 }}
        >
          <Box sx={{ p: 2 }}>
            <SettingsBox
              title="installation.submeterEditor.deviceName"
              suffix={
                <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                  <TextField
                    label={t('installation.submeterEditor.deviceName')}
                    name={'ioInterfaces.storage.name'}
                    maxLength={100}
                    fullWidth
                    disabled={!canEdit}
                  />
                </Box>
              }
            />
            <Typography
              component="h3"
              sx={{
                fontSize: 18,
                fontWeight: 'bold',
                pt: 3,
                pb: 2,
                color: theme => theme.palette.primary.dark,
                mb: 2,
                borderBottom: theme => `1px solid ${theme.palette.divider}`,
              }}
            >
              {t('installation.ioInterfaceEditor.controller')}
            </Typography>
            <SettingsBox
              title="installation.productEditor.controllerDevice"
              suffix={
                <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                  <ShellTextField
                    disabled
                    label={t('installation.productEditor.controllerDevice')}
                    value={linkedStorageDevice?.name || t('installation.types.storage')}
                  />
                </Box>
              }
            />
            <SettingsBox
              title="installation.summaryTable.deviceId"
              suffix={
                <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                  <TextField name={'ioInterfaces.storage.pairingId'} label={t('installation.summaryTable.deviceId')} disabled fullWidth />
                </Box>
              }
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {/* Pairing is done through Storage device. */}
              <PairingButton size="large" sx={{ height: '100%', minWidth: { xs: '100%', sm: 'auto' } }} disabled icon="transfer">
                {getPairingButtonLabel(t, deviceId, pairingId)}
              </PairingButton>
            </Box>

            <Typography
              component="h3"
              fontSize={18}
              fontWeight={'bold'}
              pt={3}
              pb={2}
              sx={{
                color: theme => theme.palette.primary.dark,
              }}
            >
              {t('installation.ioInterfaceEditor.controlledDevice')}
            </Typography>
            <SettingsBox
              title="installation.submeterEditor.outputsConfig"
              suffix={
                <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                  <ShellTextField
                    disabled
                    label={t('installation.submeterEditor.outputsConfig')}
                    value={t('installation.submeterEditor.outputConfig.sgReady')}
                  />
                </Box>
              }
            />
            <SettingsBox
              title="installation.submeterEditor.deviceType"
              suffix={
                <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                  <ShellTextField
                    disabled
                    label={t('installation.submeterEditor.deviceType')}
                    value={t('installation.submeterEditor.deviceTypeOptions.hvac')}
                  />
                </Box>
              }
            />
            <SettingsBox
              title="installation.submeterEditor.nominalPower"
              suffix={
                <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                  <NumberField
                    label={t('installation.submeterEditor.nominalPower')}
                    suffix={t('affixes.watt')}
                    name={'ioInterfaces.storage.nominalPower'}
                    min={0}
                    max={23_000}
                    fullWidth
                    disabled={!canEdit}
                  />
                </Box>
              }
            />
          </Box>
        </AccordionItem>
      </Box>
    </HideDirtyStateProvider>
  )
}
